import { ButtonUtils, Image3D, GridUtils, MeshUtils, ObjectBuilder, Rectangle3D } from "@dreamcam/babylon-tools";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { Env } from "../../../settings/Env";
import { text } from "../../../TextResources";
export class SubtitlePanel {
    constructor(mesh2d, controller, languageListMesh) {
        this._isLanguageListOpen = false;
        this._languages = [];
        this._languageListSignature = "";
        this._languageListSelectedCode = null;
        this._mesh2d = mesh2d;
        this._controller = controller;
        this._languageListMesh = languageListMesh;
        this._controls = this._createControls();
        const { list } = this._createLanguageListControls();
        this._languageList = list;
        MeshUtils.setVisibility(this._mesh2d, false);
        MeshUtils.setVisibility(this._languageListMesh, false);
        this._controller.subscribe({
            onStateChanged: (state) => this._applyState(state),
        });
        this._applyState(this._controller.state);
    }
    _createLanguageListControls() {
        const cfg = Env.broadcast.menu.subtitles.languageList;
        const mesh = this._languageListMesh;
        const listWidthPx = mesh.m2px(cfg.width - cfg.padding.left - cfg.padding.right);
        const root = new Rectangle3D("subtitleLanguageListRoot");
        root.thickness = 0;
        root.background = cfg.background;
        root.cornerRadius = mesh.m2px(cfg.cornerRadius);
        root.clipChildren = true;
        root.adaptHeightToChildren = true;
        root.widthInPixels = mesh.m2px(cfg.width);
        root.heightInPixels = 0;
        root.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        root.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        mesh.texture.addControl(root);
        const list = new StackPanel("subtitleLanguageList");
        list.isVertical = true;
        list.isHitTestVisible = true;
        list.adaptHeightToChildren = true;
        list.widthInPixels = listWidthPx;
        list.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        list.spacing = 0;
        list.paddingTopInPixels = mesh.m2px(cfg.padding.top);
        list.paddingBottomInPixels = mesh.m2px(cfg.padding.bottom);
        list.paddingLeftInPixels = mesh.m2px(cfg.padding.left);
        list.paddingRightInPixels = mesh.m2px(cfg.padding.right);
        root.addControl(list);
        return { root, list };
    }
    _createControls() {
        const cfg = Env.broadcast.menu.subtitles;
        const actionsColumnWidth = Math.max(cfg.closeButton.width, cfg.languagePill.width);
        const gridLayout = {
            rows: [
                "1*",
                `${cfg.closeButton.height}m`,
                `${cfg.languagePill.offsetBelowClose}m`,
                `${cfg.languagePill.height}m`,
                "1*",
            ],
            columns: [
                "1*",
                `${actionsColumnWidth}m`,
                `${cfg.closeButton.right}m`,
            ],
        };
        const grid = GridUtils.CreateGrid("subtitlePanelGrid", gridLayout, this._mesh2d);
        const textureWidthPx = this._mesh2d.m2px(this._mesh2d.width);
        const textureHeightPx = this._mesh2d.m2px(this._mesh2d.height);
        this._mesh2d.texture.idealWidth = textureWidthPx;
        this._mesh2d.texture.idealHeight = textureHeightPx;
        grid.widthInPixels = textureWidthPx;
        grid.heightInPixels = textureHeightPx;
        grid.cornerRadius = this._mesh2d.m2px(Env.broadcast.menu.cornerRadius);
        grid.thickness = 0;
        grid.background = cfg.background;
        grid.horizontalAlignment = cfg.gridControl.horizontalAlignment;
        grid.verticalAlignment = cfg.gridControl.verticalAlignment;
        grid.clipChildren = false;
        grid.isHitTestVisible = cfg.gridControl.isHitTestVisible;
        grid.adaptWidthToChildren = false;
        grid.adaptHeightToChildren = false;
        this._mesh2d.texture.addControl(grid);
        this._mesh2d.texture.rootContainer.clipChildren = false;
        const textBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "subtitleTextBlock", "", cfg.text.style);
        grid.addControl(textBlock, 0, 0, gridLayout.rows.length, gridLayout.columns.length);
        const placeholder = ObjectBuilder.createTextBlock3D(this._mesh2d, "subtitlePlaceholder", text.broadcast_menu_subtitles_placeholder, cfg.placeholder.style);
        grid.addControl(placeholder, 0, 0, gridLayout.rows.length, gridLayout.columns.length);
        const closeButton = ButtonUtils.CreateImgButton("subtitleCloseButton", {
            ...cfg.closeButton,
            hoverImg: cfg.closeButton.normalImg,
            pressedImg: cfg.closeButton.normalImg,
            top: undefined,
            left: undefined,
            horizontalAlignment: Control.HORIZONTAL_ALIGNMENT_CENTER,
            verticalAlignment: Control.VERTICAL_ALIGNMENT_CENTER,
        }, this._mesh2d);
        const closeHoverBg = new Rectangle3D("subtitleCloseButtonHoverBg");
        const closeHoverBgSize = Math.max(closeButton.widthInPixels, closeButton.heightInPixels);
        closeHoverBg.thickness = 0;
        closeHoverBg.isHitTestVisible = false;
        closeHoverBg.useArc = true;
        closeHoverBg.background = "transparent";
        closeHoverBg.widthInPixels = closeHoverBgSize;
        closeHoverBg.heightInPixels = closeHoverBgSize;
        closeHoverBg.cornerRadius = Math.round(closeHoverBgSize / 2);
        closeHoverBg.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        closeHoverBg.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        grid.addControl(closeHoverBg, 1, 1);
        closeButton.stateController.changeState("normal");
        const closeHoverColor = cfg.languagePill.button.hoverState.color;
        const closePressedColor = cfg.languagePill.button.pressedState.color ?? closeHoverColor;
        const setCloseBg = (mode) => {
            if (mode === "normal")
                closeHoverBg.background = "transparent";
            else if (mode === "pressed")
                closeHoverBg.background = closePressedColor;
            else
                closeHoverBg.background = closeHoverColor;
        };
        closeButton.onPointerEnterObservable.add(() => setCloseBg("hover"));
        closeButton.onPointerOutObservable.add(() => setCloseBg("normal"));
        closeButton.onPointerDownObservable.add(() => setCloseBg("pressed"));
        closeButton.onPointerUpObservable.add(() => setCloseBg("hover"));
        closeButton.onClickObserver.add(() => this._controller.toggle());
        grid.addControl(closeButton, 1, 1);
        const languagePill = ButtonUtils.CreateImgButton("subtitleLanguagePill", {
            ...cfg.languagePill.button,
            width: cfg.languagePill.width,
            height: cfg.languagePill.height,
            useArc: true,
            horizontalAlignment: Control.HORIZONTAL_ALIGNMENT_CENTER,
            verticalAlignment: Control.VERTICAL_ALIGNMENT_CENTER,
        }, this._mesh2d);
        grid.addControl(languagePill, 3, 1);
        const languageLabel = ObjectBuilder.createTextBlock3D(this._mesh2d, "subtitleLanguageLabel", "", cfg.languageLabel.style);
        languagePill.addControl(languageLabel);
        languagePill.onClickObserver.add(() => this._toggleLanguageList());
        return {
            textBlock,
            placeholder,
            closeButton,
            languagePill,
            languageLabel,
        };
    }
    _applyState(state) {
        const visible = state.windowVisible && state.availability === "available" && state.isOn;
        MeshUtils.setVisibility(this._mesh2d, visible);
        /*console.log("[Subtitles] panel state", {
            visible,
            availability: state.availability,
            isOn: state.isOn,
            selectedLanguage: state.selectedLanguage,
        });*/
        const hasText = state.lastText !== null && state.lastText.text !== "";
        const lastText = state.lastText?.text ?? "";
        this._controls.textBlock.text = lastText;
        this._controls.textBlock.isVisible = hasText;
        this._controls.placeholder.isVisible = !hasText;
        this._languages = state.languages;
        const selectedCode = state.selectedLanguage ?? state.languages[0]?.code ?? "EN";
        const two = selectedCode.slice(0, 2).toLowerCase();
        this._controls.languageLabel.text = two.length === 0 ? "" : `${two[0].toUpperCase()}${two.slice(1)}`;
        const hasMultipleLanguages = state.languages.length > 1;
        this._controls.languagePill.isEnabled = hasMultipleLanguages;
        if (!hasMultipleLanguages) {
            this._isLanguageListOpen = false;
            this._languageListSignature = "";
            this._languageListSelectedCode = null;
        }
        this._syncLanguagePillAppearance(hasMultipleLanguages && this._isLanguageListOpen);
        MeshUtils.setVisibility(this._languageListMesh, visible && hasMultipleLanguages && this._isLanguageListOpen);
        if (hasMultipleLanguages) {
            const languageListSignature = this._getLanguageListSignature(state.languages);
            if (languageListSignature !== this._languageListSignature || selectedCode !== this._languageListSelectedCode) {
                this._languageListSignature = languageListSignature;
                this._languageListSelectedCode = selectedCode;
                this._rebuildLanguageList(selectedCode);
            }
        }
        else {
            this._clearLanguageList();
        }
    }
    _toggleLanguageList() {
        const hasMultipleLanguages = this._languages.length > 1;
        if (!hasMultipleLanguages)
            return;
        this._isLanguageListOpen = !this._isLanguageListOpen;
        this._syncLanguagePillAppearance(this._isLanguageListOpen);
        MeshUtils.setVisibility(this._languageListMesh, this._isLanguageListOpen);
    }
    _clearLanguageList() {
        while (this._languageList.children.length > 0) {
            const child = this._languageList.children[0];
            this._languageList.removeControl(child);
            child.dispose();
        }
    }
    _getLanguageListSignature(languages) {
        return languages.map(lang => `${lang.code}:${lang.label}`).join("|");
    }
    _syncLanguagePillAppearance(isActive) {
        const buttonCfg = Env.broadcast.menu.subtitles.languagePill.button;
        const activeState = buttonCfg.hoverState;
        this._controls.languagePill.changeLayout({
            ...buttonCfg,
            useArc: true,
            normalState: isActive ? activeState : buttonCfg.normalState,
            hoverState: activeState,
            pressedState: isActive ? activeState : buttonCfg.pressedState,
        });
        this._controls.languagePill.stateController.changeState(isActive ? "hover" : "normal");
    }
    _rebuildLanguageList(selected) {
        this._clearLanguageList();
        const cfg = Env.broadcast.menu.subtitles.languageList;
        const rowWidthMeters = cfg.width - cfg.padding.left - cfg.padding.right;
        const rowHoverBackground = Env.broadcast.menu.subtitles.languagePill.button.hoverState.color ?? cfg.row.hoverBackground;
        this._languages.forEach(lang => {
            const isSelected = lang.code === selected;
            const row = ButtonUtils.CreateImgButton(`subtitleLang_${lang.code}`, {
                width: rowWidthMeters,
                height: cfg.row.height,
                cornerRadiusPx: cfg.row.cornerRadius,
                useArc: true,
                common: { borderThickness: 0 },
                normalState: {
                    color: cfg.row.background,
                },
                hoverState: {
                    color: rowHoverBackground,
                },
                pressedState: {
                    color: rowHoverBackground,
                },
            }, this._languageListMesh);
            row.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
            row.onClickObserver.add(() => {
                this._controller.setLanguage(lang.code);
                this._isLanguageListOpen = false;
                MeshUtils.setVisibility(this._languageListMesh, false);
            });
            const basePaddingLeft = this._languageListMesh.m2px(cfg.row.padding.left);
            const basePaddingRight = this._languageListMesh.m2px(cfg.row.padding.right);
            const checkReserve = this._languageListMesh.m2px(cfg.row.fontSize);
            const checkIconSize = Math.round(checkReserve * 1.7);
            const checkGap = Math.round(checkReserve * 0.5);
            const contentGrid = GridUtils.CreateGrid(`subtitleLangContent_${lang.code}`, {
                rows: ["1*"],
                columns: [
                    `${cfg.row.padding.left}m`,
                    `${cfg.row.fontSize}m`,
                    `${(cfg.row.fontSize * 0.5)}m`,
                    "1*",
                    `${cfg.row.padding.right}m`,
                ],
            }, this._languageListMesh);
            contentGrid.widthInPixels = row.widthInPixels;
            contentGrid.heightInPixels = row.heightInPixels;
            contentGrid.thickness = 0;
            contentGrid.background = "transparent";
            contentGrid.isHitTestVisible = false;
            row.addControl(contentGrid);
            const label = ObjectBuilder.createTextBlock3D(this._languageListMesh, `subtitleLangLabel_${lang.code}`, lang.label, cfg.row.textStyle);
            label.color = cfg.row.color;
            label.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
            label.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
            label.widthInPixels = row.widthInPixels - basePaddingLeft - basePaddingRight - checkReserve - checkGap;
            contentGrid.addControl(label, 0, 3);
            if (isSelected) {
                const checkIcon = new Image3D(`subtitleLangCheck_${lang.code}`, cfg.row.checkIcon);
                checkIcon.widthInPixels = checkIconSize;
                checkIcon.heightInPixels = checkIconSize;
                checkIcon.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
                checkIcon.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
                contentGrid.addControl(checkIcon, 0, 1);
            }
            this._languageList.addControl(row);
        });
    }
}
